import React from 'react';

export default function HowWeWork({ activeStep, setActiveStep }) {
    return (
        <section className="bg-white pt-8 sm:pt-12 pb-16 sm:pb-24 relative overflow-hidden">
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 sm:mb-20 gap-6 sm:gap-8">
                    <h2 className="text-[#0054DB] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold">
                        How We Work
                    </h2>
                    <div className="lg:text-center max-w-lg">
                        <p className="text-gray-500 text-lg leading-relaxed">
                            We simplifies the process of product size selection and order tracking, eliminating stress and saving time.
                        </p>
                    </div>
                    <a href="#book-call" className="bg-[#0054DB] text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors inline-block cursor-pointer">
                        Book a Call
                    </a>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    {/* Accordion */}
                    <div className="flex flex-col gap-4">
                        {[
                            {
                                id: 'discover',
                                title: 'Discover',
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                ),
                                content: 'Before we speak, we listen. Your product, market, ambition — understood fully before we build anything.'
                            },
                            {
                                id: 'blueprint',
                                title: 'Blueprint',
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                ),
                                content: 'We architect a comprehensive roadmap tailored to your specific goals, ensuring a solid foundation for growth.'
                            },
                            {
                                id: 'build',
                                title: 'Build & Launch',
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                ),
                                content: 'Our expert team brings the vision to life with precision engineering and design, launching with impact.'
                            },
                            {
                                id: 'scale',
                                title: 'Scale',
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                    </svg>
                                ),
                                content: 'We optimize and expand your solution, implementing data-driven strategies for sustainable long-term growth.'
                            }
                        ].map((step) => (
                            <div
                                key={step.id}
                                onClick={() => setActiveStep(step.id)}
                                className={`border rounded-2xl transition-all duration-300 cursor-pointer overflow-hidden ${activeStep === step.id
                                    ? 'bg-white border-blue-100 shadow-lg ring-1 ring-blue-50'
                                    : 'bg-white border-gray-100 hover:border-blue-50'
                                    }`}
                            >
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${activeStep === step.id ? 'bg-[#0054DB] text-white' : 'bg-gray-50 text-gray-500'
                                                }`}>
                                                {step.icon}
                                            </div>
                                            <h3 className={`text-xl font-medium transition-colors ${activeStep === step.id ? 'text-black' : 'text-gray-500'
                                                }`}>
                                                {step.title}
                                            </h3>
                                        </div>
                                        <div className={`transition-transform duration-300 ${activeStep === step.id ? 'rotate-180' : ''}`}>
                                            {activeStep === step.id ? (
                                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>

                                    {/* Content - Collapsible */}
                                    <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${activeStep === step.id ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'
                                        }`}>
                                        <div className="overflow-hidden">
                                            <div className={`pl-[56px] text-gray-500 leading-relaxed border-t pt-4 ${activeStep === step.id ? 'border-blue-50' : 'border-transparent'}`}>
                                                {step.content}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Interactive Line */}
                                <div className={`h-1 bg-[#0054DB] transition-all duration-300 ${activeStep === step.id ? 'w-full' : 'w-0'
                                    }`}></div>
                            </div>
                        ))}
                    </div>

                    {/* Feature Image */}
                    <div className="relative h-[300px] sm:h-[400px] lg:h-full lg:min-h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden group">
                        <div className="absolute inset-0 bg-gray-900/10 z-10 group-hover:bg-gray-900/0 transition-colors duration-500"></div>
                        {/* Actual Image */}
                        <img
                            src="/images/work-culture.jpg"
                            alt="Zenruva Work Culture"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
